import React from 'react';
import { useApolloClient } from '@apollo/client';
/**
 * Returns a stable function to update Apollo cache for a specific query+variables pair.
 *
 * Parameters to the returned function:
 * - query: The GraphQL DocumentNode used when reading/writing the cache
 * - variables: Variables object used with the query when reading/writing the cache
 * - updater: Callback receiving the current cached value (or null if missing) and returning the new value
 *
 * Return value:
 * - void (side-effect: updates Apollo cache in-place)
 *
 * Example usage:
 * const updateCache = useApolloCacheUpdate();
 * updateCache<{ Items: Item[] }, { id: string }>(MyQuery, { id }, current => ({
 *   Items: [...(current?.Items ?? []), newItem]
 * }));
 */
export const useApolloCacheUpdate = () => {
    const apolloClient = useApolloClient();
    const updateCache = React.useCallback((query, variables, updater) => {
        // Apollo 3.8+ wraps cache reads/writes in `Unmasked<T>` to support data masking.
        // We don't use masked fragments, so the runtime shape is identical to `TData`.
        apolloClient.cache.updateQuery({ query, variables }, existing => updater(existing));
    }, [apolloClient]);
    return updateCache;
};
